import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { hasSuperAdminAccess } from '@/entities/user'
import { useAuth } from '@/features/auth'
import { routes } from '@/shared/config'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'
import { Button, SegmentedTabs, type SegmentedTabItem } from '@/shared/ui'
import { VideoLibraryAdminWorkspace, type AdminVideoTab } from './video-library-admin-workspace'

type AdminPrimaryTab = 'subscription' | 'video'

const primaryTabs: SegmentedTabItem<AdminPrimaryTab>[] = [
  { id: 'subscription', label: 'Подписка' },
  { id: 'video', label: 'Видео' },
]

const videoTabs: SegmentedTabItem<AdminVideoTab>[] = [
  { id: 'user-access', label: 'Доступ пользователей' },
  { id: 'bundles', label: 'Бандлы' },
  { id: 'categories', label: 'Категории' },
  { id: 'custom-filters', label: 'Кастомные фильтры' },
  { id: 'resources', label: 'Ресурсы' },
  { id: 'collections', label: 'Коллекции' },
  { id: 'videos', label: 'Видео' },
]

const titleStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.heading.h2),
}

const bodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.base),
}

const mutedStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.secondary,
  ...typographyStyle(designTokens.typography.body.sm),
}

const parsePrimaryTab = (value: string | null): AdminPrimaryTab =>
  value === 'subscription' || value === 'video' ? value : 'video'

const parseVideoTab = (value: string | null): AdminVideoTab =>
  videoTabs.some((tab) => tab.id === value) ? (value as AdminVideoTab) : 'user-access'

export function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { logout, user } = useAuth()
  const showSuperAdminShortcut = hasSuperAdminAccess(user)

  const primaryTab = parsePrimaryTab(searchParams.get('tab'))
  const videoTab = parseVideoTab(searchParams.get('videoTab'))

  const setPrimaryTab = (nextTab: AdminPrimaryTab) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('tab', nextTab)

    if (nextTab === 'video' && !nextParams.get('videoTab')) {
      nextParams.set('videoTab', 'user-access')
    }

    setSearchParams(nextParams, { replace: true })
  }

  const setVideoTab = (nextTab: AdminVideoTab) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('tab', 'video')
    nextParams.set('videoTab', nextTab)
    setSearchParams(nextParams, { replace: true })
  }

  useEffect(() => {
    document.title = 'Admin Console | Epic Growth'
  }, [])

  return (
    <section
      className="mx-auto flex w-full max-w-[1360px] flex-col px-4 py-6 lg:px-8 lg:py-8"
      style={{ gap: designTokens.spacing.lg }}
    >
      <header
        className="flex flex-col rounded-[32px] border bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.08)] lg:p-8"
        style={{
          gap: designTokens.spacing.lg,
          borderColor: designTokens.colors.border.adminPattern,
        }}
      >
        <div
          className="flex flex-wrap items-center justify-between"
          style={{ gap: designTokens.spacing.sm }}
        >
          <h1 style={titleStyle}>Панель управления контентом</h1>
          <div className="flex flex-wrap gap-3">
            <Link to={routes.dashboardLk}>
              <Button type="button" variant="secondaryAssist">
                Вернуться в кабинет
              </Button>
            </Link>
            {showSuperAdminShortcut ? (
              <Link to={routes.superAdminUsers}>
                <Button type="button" variant="secondaryAssist">
                  Super Admin
                </Button>
              </Link>
            ) : null}
            <Button type="button" variant="primary" onClick={() => void logout()}>
              Выйти
            </Button>
          </div>
        </div>
      </header>

      <section
        className="rounded-[32px] border bg-white p-6 shadow-[0_18px_60px_rgba(15,23,42,0.06)] lg:p-8"
        style={{
          gap: designTokens.spacing.lg,
          borderColor: designTokens.colors.border.adminPattern,
        }}
      >
        <div className="flex flex-col gap-4">
          <SegmentedTabs
            activeId={primaryTab}
            ariaLabel="Admin primary tabs"
            items={primaryTabs}
            onChange={setPrimaryTab}
          />

          {primaryTab === 'subscription' ? (
            <div
              className="rounded-[28px] border p-6"
              style={{
                borderColor: designTokens.colors.border.adminPattern,
                backgroundColor: '#faf8f4',
              }}
            >
              <div className="flex flex-col gap-3">
                <h2
                  style={{
                    fontFamily: designTokens.typography.fontFamilies.primary,
                    color: designTokens.colors.text.strong,
                    ...typographyStyle(designTokens.typography.heading.h2),
                  }}
                >
                  Подписка
                </h2>
                <p style={bodyStyle}>
                  Вкладка зарезервирована под subscription-management. В текущем объеме задачи она
                  остается намеренно пустой, чтобы не смешивать ее с video-library CRUD.
                </p>
                <p style={mutedStyle}>
                  Следующий активный трек сейчас находится во вкладке `Видео`.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="flex flex-col gap-3">
                <span style={mutedStyle}>Video workspace</span>
                <SegmentedTabs
                  activeId={videoTab}
                  ariaLabel="Admin video subtabs"
                  items={videoTabs}
                  onChange={setVideoTab}
                />
              </div>

              <VideoLibraryAdminWorkspace activeTab={videoTab} />
            </div>
          )}
        </div>
      </section>
    </section>
  )
}

AdminPage.displayName = 'AdminPage'
